import { z } from 'zod';

/**
 * Campos que un tipo de activo puede pedir al registrarlo (p. ej. MAC para un equipo,
 * licencias para un software). Cada tipo elige cuáles usa y cuáles son obligatorios.
 * El valor de cada campo se guarda por activo en `activo_atributos`.
 */
export const CAMPOS_ACTIVO = {
  mac: { etiqueta: 'Dirección MAC', tipo: 'texto' },
  ip: { etiqueta: 'Dirección IP', tipo: 'texto' },
  dhcp: { etiqueta: 'Usa DHCP', tipo: 'si_no' },
  custodia_tecnica: { etiqueta: 'Custodio técnico', tipo: 'texto' },
  usuario_asignado: { etiqueta: 'Usuario asignado', tipo: 'texto' },
  clasificacion_informacion: {
    etiqueta: 'Clasificación máxima de la información',
    tipo: 'lista',
    opciones: ['Pública', 'Interna', 'Confidencial', 'Restringida'],
  },
  cifrado_disco: { etiqueta: 'Cifrado de disco', tipo: 'si_no' },
  agente_edr: { etiqueta: 'Agente EDR / antimalware', tipo: 'si_no' },
  proveedor: { etiqueta: 'Proveedor', tipo: 'texto' },
  fecha_instalacion: { etiqueta: 'Fecha de instalación', tipo: 'fecha' },
  numero_licencias: { etiqueta: 'N.º de licencias', tipo: 'numero' },
  grupo: { etiqueta: 'Grupo', tipo: 'texto' },
  ultima_revision: { etiqueta: 'Última revisión', tipo: 'fecha' },
  proxima_revision: { etiqueta: 'Próxima revisión', tipo: 'fecha' },
} as const;

export type CampoActivo = keyof typeof CAMPOS_ACTIVO;
export type TipoCampoActivo = (typeof CAMPOS_ACTIVO)[CampoActivo]['tipo'];

export const CODIGOS_CAMPO_ACTIVO = Object.keys(CAMPOS_ACTIVO) as [CampoActivo, ...CampoActivo[]];
export const campoActivoSchema = z.enum(CODIGOS_CAMPO_ACTIVO);

/** Valida y normaliza el valor de un campo. Devuelve el texto a guardar o un mensaje de error. */
export function valorCampoActivo(campo: CampoActivo, bruto: string): { valor: string } | { error: string } {
  const definicion = CAMPOS_ACTIVO[campo];
  const valor = bruto.trim();
  switch (definicion.tipo) {
    case 'texto':
      return valor.length > 300 ? { error: `${definicion.etiqueta}: máximo 300 caracteres.` } : { valor };
    case 'si_no':
      if (valor !== 'si' && valor !== 'no') return { error: `${definicion.etiqueta}: elija Sí o No.` };
      return { valor };
    case 'fecha':
      if (!/^\d{4}-\d{2}-\d{2}$/.test(valor) || Number.isNaN(Date.parse(valor))) {
        return { error: `${definicion.etiqueta}: fecha no válida.` };
      }
      return { valor };
    case 'numero':
      if (!/^\d{1,9}$/.test(valor)) return { error: `${definicion.etiqueta}: ingrese un número entero.` };
      return { valor };
    case 'lista':
      return (definicion.opciones as readonly string[]).includes(valor)
        ? { valor }
        : { error: `${definicion.etiqueta}: opción no válida.` };
  }
}
